import { useCallback, useState } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  IconClose,
  InformationalScreen,
  LoadingModal,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { AxiosError } from 'axios';
import { Button, Footer, MainContainer, OptInOut, RichText } from 'Components/utility';
import { ApiStatus, APP_RETURN_TIMEOUT, ErrorCode } from 'Constants';
import { useApiError } from 'Hooks';
import { AUTH_SESSION } from 'Reducers/auth/types';
import { type RootState } from 'Reducers/index';
import { handleConsent } from 'Services/consent';
import { notifyApp } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { logout } from 'Utilities/session';
import { store } from 'Utilities/store';

const mapStateToProps = ({
  env: { osType, osVersion, app, appVersion },
  auth,
  nav: { query },
}: RootState) => ({
  osType,
  osVersion,
  app,
  appVersion,
  auth,
  isAccountProfile: !!query.accountProfile,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const HipaaConsent = ({ osType, osVersion, isAccountProfile, app, appVersion, auth }: Props) => {
  const [loading, setLoading] = useState(false);

  const { showApiErrorModal, showNetworkErrorModal } = useApiError();

  const handleApiRequestError = useCallback(
    (error: AxiosError<ApiErrorData>) => {
      if (error.status === ApiStatus.UNAUTHORIZED) {
        logout();
      } else if (error.code === ErrorCode.NETWORK_ERROR) {
        showNetworkErrorModal();
      } else {
        showApiErrorModal();
      }
    },
    [showApiErrorModal, showNetworkErrorModal]
  );

  const handleConsentWrapper = useCallback(
    async (action: string) => {
      try {
        const resp = await handleConsent(
          auth.account_id,
          action,
          osType,
          osVersion,
          app,
          appVersion,
          'hipaa'
        );
        const updatedAuthSession: AuthSession = {
          ...auth,
          include: {
            patient: {
              ...auth.include.patient,
              consents: { ...auth.include.patient.consents, hipaa: { ...resp } },
            },
          },
        };

        store.dispatch({ type: AUTH_SESSION, authSession: updatedAuthSession });

        notifyApp('adc-webview:consents', {
          id: 'hipaa',
          action,
          location: isAccountProfile ? 'profile' : 'registration',
        });

        return updatedAuthSession;
      } catch (error) {
        handleApiRequestError(error as AxiosError<ApiErrorData>);
        throw error;
      }
    },
    [app, appVersion, auth, handleApiRequestError, isAccountProfile, osType, osVersion]
  );

  const handleSubmitHipaaAction = useCallback(
    async (action: string) => {
      try {
        setLoading(true);

        const updatedAuthSession = await handleConsentWrapper(action);

        if (!auth.include.patient.consents?.realWorldEvidence?.at) {
          mediator.publish('router:navigate', '/rwe');

          return;
        }

        notifyApp<AuthSession>('adc-webview:signin', updatedAuthSession);

        // This timeout is added to give the app time to redirect the user back.
        setTimeout(() => {
          setLoading(false);
        }, APP_RETURN_TIMEOUT);
      } catch {
        setLoading(false);
      }
    },
    [auth, handleConsentWrapper]
  );

  const onClosePressed = useCallback(() => {
    mediator.publish('router:back');
    notifyApp('adc-webview:navigate-back-app');
  }, []);

  return (
    <MainContainer>
      {loading && <LoadingModal aria-label="Loading" />}
      <InformationalScreen>
        <InformationalScreen.Page
          topNavigationProps={
            isAccountProfile
              ? {
                  animatedTitle: true,
                  title: i18n.t('Hipaa.title'),
                  buttonStart: {
                    element: <IconClose testID="Hipaa.close-Pressable" />,
                    label: i18n.t('Hipaa.close'),
                    onPress: onClosePressed,
                  },
                  testID: 'Hipaa.title',
                }
              : {
                  title: undefined,
                  testID: 'Hipaa.actionTitle',
                }
          }
        >
          <InformationalScreen.Content>
            {!isAccountProfile ? (
              <Text type="$h1.strong" testID="Hipaa.title">
                {i18n.t('Hipaa.title')}
              </Text>
            ) : undefined}
            <RichText text={i18n.t('Hipaa.content.hipaaRichText.bodyText')} />
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            {!isAccountProfile ? (
              <Footer>
                <Stack alignSelf="flex-end" width="100%" gap="$4">
                  <Button
                    onPress={() => handleSubmitHipaaAction('accept')}
                    testID="Hipaa.accept"
                    aria-label={i18n.t('Global.microcopy.common.accept')}
                    label={i18n.t('Global.microcopy.common.accept')}
                  />
                  <Button
                    variant="tertiary"
                    onPress={() => handleSubmitHipaaAction('decline')}
                    label={i18n.t('Global.microcopy.common.decline')}
                    aria-label={i18n.t('Global.microcopy.common.decline')}
                    testID="Hipaa.decline"
                  />
                </Stack>
              </Footer>
            ) : (
              <OptInOut
                optionStrings={{
                  titleTag: 'Global.microcopy.common.status',
                  positiveTag: 'Global.microcopy.common.accepted',
                  negativeTag: 'Global.microcopy.common.declined',
                  positiveOption: 'Global.microcopy.common.optIn',
                  negativeOption: 'Global.microcopy.common.decline',
                }}
                initialState={
                  auth.include.patient?.consents?.hipaa?.accepted ? 'accept' : 'decline'
                }
                handleConsent={handleConsentWrapper}
              />
            )}
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(HipaaConsent);
